import React, { useEffect, useState } from 'react';
import {
  Box, Card, Table, TableHead, TableRow, TableCell, TableBody,
  Button, IconButton, Dialog, DialogTitle, DialogContent, DialogActions,
  TextField, MenuItem, Grid, Alert, Typography, Chip, InputAdornment,
  useMediaQuery, useTheme
} from '@mui/material';
import { Add, Edit, Search, AccountBalance } from '@mui/icons-material';
import api from '../../services/api';
import PageHeader from '../common/PageHeader';
import { MobileCard, MobileRow } from '../common/MobileCard';

const CURRENCIES = ['USD', 'EUR', 'GBP', 'CNY', 'JPY', 'AED', 'SGD', 'HKD', 'AUD', 'CAD'];
const COUNTRIES  = ['China', 'USA', 'UK', 'Germany', 'Japan', 'UAE', 'Singapore', 'Hong Kong', 'Australia', 'Canada', 'India', 'Taiwan', 'South Korea'];

const EMPTY = {
  name: '', address: '', city: '', country: 'China', currency: 'USD',
  bank_name: '', bank_address: '', swift_code: '', account_number: '', iban: '',
  contact_person: '', contact_email: '', contact_phone: ''
};

export default function Beneficiaries() {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [list, setList] = useState([]);
  const [filtered, setFiltered] = useState([]);
  const [search, setSearch] = useState('');
  const [dialog, setDialog] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const load = async () => {
    const res = await api.get('/beneficiaries');
    setList(res.data.data);
    setFiltered(res.data.data);
  };
  useEffect(() => { load(); }, []);
  useEffect(() => {
    setFiltered(list.filter(b =>
      b.name.toLowerCase().includes(search.toLowerCase()) ||
      b.country?.toLowerCase().includes(search.toLowerCase())
    ));
  }, [search, list]);

  const openCreate = () => { setEditing(null); setForm(EMPTY); setError(''); setDialog(true); };
  const openEdit   = (b) => { setEditing(b); setForm({ ...EMPTY, ...b }); setError(''); setDialog(true); };

  const handleSave = async () => {
    if (!form.name || !form.country || !form.currency) { setError('Name, country and currency required'); return; }
    setLoading(true);
    try {
      if (editing) await api.put(`/beneficiaries/${editing.id}`, form);
      else         await api.post('/beneficiaries', form);
      setDialog(false);
      load();
    } catch (err) {
      setError(err.response?.data?.message || 'Error saving');
    } finally { setLoading(false); }
  };

  const f = (field) => ({ value: form[field], onChange: (e) => setForm({ ...form, [field]: e.target.value }) });

  return (
    <Box>
      <PageHeader title="Beneficiary Master" subtitle="Manage suppliers and their banking details"
        action={<Button variant="contained" startIcon={<Add />} onClick={openCreate} size={isMobile ? 'small' : 'medium'}>
          {isMobile ? 'Add' : 'Add Beneficiary'}
        </Button>}
      />

      {/* Search */}
      <Box sx={{ mb: 2 }}>
        <TextField size="small" fullWidth placeholder="Search by name or country…" value={search}
          onChange={(e) => setSearch(e.target.value)}
          InputProps={{ startAdornment: <InputAdornment position="start"><Search fontSize="small" /></InputAdornment> }}
        />
      </Box>

      {/* Mobile cards */}
      {isMobile ? (
        <Box>
          {filtered.length === 0 ? (
            <Card><Box sx={{ p: 3, textAlign: 'center', color: 'text.secondary' }}>No beneficiaries found</Box></Card>
          ) : filtered.map(b => (
            <MobileCard key={b.id} onClick={() => openEdit(b)}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1 }}>
                <Box>
                  <Typography variant="body2" fontWeight={700}>{b.name}</Typography>
                  <Typography variant="caption" color="text.secondary">{b.city ? `${b.city}, ` : ''}{b.country}</Typography>
                </Box>
                <Box sx={{ display: 'flex', gap: 0.5, flexShrink: 0 }}>
                  <Chip label={b.currency} size="small" variant="outlined" />
                  <IconButton size="small"><Edit fontSize="small" /></IconButton>
                </Box>
              </Box>
              {b.bank_name    && <MobileRow label="Bank"    value={b.bank_name} />}
              {b.swift_code   && <MobileRow label="SWIFT"   value={b.swift_code} mono />}
              {b.account_number && <MobileRow label="Account" value={b.account_number} mono />}
            </MobileCard>
          ))}
        </Box>
      ) : (
        /* Desktop table */
        <Card>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Name</TableCell>
                <TableCell>Country</TableCell>
                <TableCell>Currency</TableCell>
                <TableCell>Bank</TableCell>
                <TableCell>SWIFT</TableCell>
                <TableCell>Account No.</TableCell>
                <TableCell>Contact</TableCell>
                <TableCell align="center">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {filtered.length === 0 ? (
                <TableRow><TableCell colSpan={8} align="center" sx={{ py: 4, color: 'text.secondary' }}>No beneficiaries found</TableCell></TableRow>
              ) : filtered.map(b => (
                <TableRow key={b.id} hover>
                  <TableCell>
                    <Typography variant="body2" fontWeight={600}>{b.name}</Typography>
                    {b.city && <Typography variant="caption" color="text.secondary">{b.city}</Typography>}
                  </TableCell>
                  <TableCell sx={{ fontSize: 13 }}>{b.country}</TableCell>
                  <TableCell><Chip label={b.currency} size="small" variant="outlined" /></TableCell>
                  <TableCell sx={{ fontSize: 12, maxWidth: 140, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{b.bank_name || '—'}</TableCell>
                  <TableCell sx={{ fontSize: 12, fontFamily: 'monospace' }}>{b.swift_code || '—'}</TableCell>
                  <TableCell sx={{ fontSize: 12, fontFamily: 'monospace' }}>{b.account_number || '—'}</TableCell>
                  <TableCell sx={{ fontSize: 12 }}>{b.contact_person || '—'}</TableCell>
                  <TableCell align="center">
                    <IconButton size="small" onClick={() => openEdit(b)}><Edit fontSize="small" /></IconButton>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Card>
      )}

      {/* Dialog */}
      <Dialog open={dialog} onClose={(_, r) => { if (r !== 'backdropClick' && r !== 'escapeKeyDown') setDialog(false); }} maxWidth="md" fullWidth fullScreen={isMobile}>
        <DialogTitle>{editing ? 'Edit Beneficiary' : 'Add New Beneficiary'}</DialogTitle>
        <DialogContent>
          {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
          <Typography variant="subtitle2" color="primary" sx={{ mt: 1, mb: 1.5 }}>Basic Details</Typography>
          <Grid container spacing={2}>
            <Grid item xs={12}><TextField label="Beneficiary / Company Name *" fullWidth {...f('name')} /></Grid>
            <Grid item xs={12}><TextField label="Address" fullWidth multiline rows={2} {...f('address')} /></Grid>
            <Grid item xs={6}><TextField label="City" fullWidth {...f('city')} /></Grid>
            <Grid item xs={6}>
              <TextField label="Country *" select fullWidth {...f('country')}>
                {COUNTRIES.map(c => <MenuItem key={c} value={c}>{c}</MenuItem>)}
              </TextField>
            </Grid>
            <Grid item xs={6}>
              <TextField label="Currency *" select fullWidth {...f('currency')}>
                {CURRENCIES.map(c => <MenuItem key={c} value={c}>{c}</MenuItem>)}
              </TextField>
            </Grid>
          </Grid>
          <Typography variant="subtitle2" color="primary" sx={{ mt: 3, mb: 1.5 }}>Banking Details</Typography>
          <Grid container spacing={2}>
            <Grid item xs={12}><TextField label="Bank Name" fullWidth {...f('bank_name')} /></Grid>
            <Grid item xs={12}><TextField label="Bank Address" fullWidth multiline rows={2} {...f('bank_address')} /></Grid>
            <Grid item xs={6}><TextField label="SWIFT Code" fullWidth {...f('swift_code')} /></Grid>
            <Grid item xs={6}><TextField label="Account Number" fullWidth {...f('account_number')} /></Grid>
            <Grid item xs={6}><TextField label="IBAN" fullWidth {...f('iban')} /></Grid>
          </Grid>
          <Typography variant="subtitle2" color="primary" sx={{ mt: 3, mb: 1.5 }}>Contact</Typography>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={4}><TextField label="Contact Person" fullWidth {...f('contact_person')} /></Grid>
            <Grid item xs={12} sm={4}><TextField label="Email" fullWidth {...f('contact_email')} /></Grid>
            <Grid item xs={12} sm={4}><TextField label="Phone" fullWidth {...f('contact_phone')} /></Grid>
          </Grid>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setDialog(false)}>Cancel</Button>
          <Button variant="contained" onClick={handleSave} disabled={loading}>{editing ? 'Update' : 'Save'}</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
